import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, 
  RefreshCw, 
  AlertCircle, 
  ShieldCheck, 
  Clock, 
  AlertTriangle, 
  Filter, 
  ExternalLink,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  Layers,
  ArrowRight,
  Users,
  Repeat,
  PlusCircle,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Calendar
} from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { carteraAPI, describirError } from '../services/api';
import '../styles/Pages.css';

const TRAMO_COLORS = {
  AL_CORRIENTE: { bg: '#dcfce7', text: '#166534', border: '#86efac', bar: '#22c55e' },
  '1-30': { bg: '#fef9c3', text: '#854d0e', border: '#fde047', bar: '#eab308' },
  '31-60': { bg: '#ffedd5', text: '#9a3412', border: '#fdba74', bar: '#f97316' },
  '61-90': { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5', bar: '#ef4444' },
  '91+': { bg: '#fdf2f8', text: '#831843', border: '#f472b6', bar: '#db2777' }
};

const TRAMO_LABELS = {
  AL_CORRIENTE: 'Al corriente (0 días)',
  '1-30': '1–30 días',
  '31-60': '31–60 días',
  '61-90': '61–90 días',
  '91+': '91+ días'
};

// Componente Skeleton para evitar mostrar ceros falsos durante la carga
const MetricSkeleton = ({ width = '100px', height = '24px' }) => (
  <div 
    style={{ 
      width, 
      height, 
      backgroundColor: '#e2e8f0', 
      borderRadius: '4px', 
      animation: 'pulse 1.5s infinite ease-in-out',
      marginTop: '4px'
    }} 
  />
);

function Dashboard() {
  const navigate = useNavigate();
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historicoAbierto, setHistoricoAbierto] = useState(false);

  // Estado para el Modal / Vista Completa de Cartera
  const [modalCarteraAbierto, setModalCarteraAbierto] = useState(false);
  const [carteraCompleta, setCarteraCompleta] = useState([]);
  const [loadingCompleta, setLoadingCompleta] = useState(false);
  const [busquedaCompleta, setBusquedaCompleta] = useState('');
  const [filtroTramoCompleta, setFiltroTramoCompleta] = useState('');
  const [filtroTipoCompleta, setFiltroTipoCompleta] = useState('');
  const [paginaCompleta, setPaginaCompleta] = useState(1);
  const [limiteCompleta, setLimiteCompleta] = useState(25);
  const [paginacionCompleta, setPaginacionCompleta] = useState({ total: 0, totalPages: 1 });

  // 1. Cargar datos principales del Dashboard (1 SOLA LLAMADA CONSOLIDADA)
  const cargarDashboard = useCallback(async (forzar = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await carteraAPI.resumen(forzar ? { params: { refresh: 'true' } } : {});
      if (res.data) {
        setResumen(res.data);
      }
    } catch (err) {
      const desc = describirError(err);
      setError(desc.mensaje || 'No fue posible cargar el Dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarDashboard();
  }, [cargarDashboard]);

  // 2. Cargar Cartera Completa Paginada (Modal / Drawer)
  const cargarCarteraCompleta = useCallback(async () => {
    setLoadingCompleta(true);
    try {
      const res = await carteraAPI.aging({
        page: paginaCompleta,
        pageSize: limiteCompleta,
        search: busquedaCompleta.trim() || undefined,
        bucket: filtroTramoCompleta || undefined,
        tipoCredito: filtroTipoCompleta || undefined,
        sortBy: 'diasVencidos',
        sortOrder: 'desc'
      });

      if (res.data && Array.isArray(res.data.data)) {
        setCarteraCompleta(res.data.data);
        setPaginacionCompleta(res.data.pagination || { total: res.data.data.length, totalPages: 1 });
      }
    } catch (err) {
      console.error('Error al cargar cartera completa:', err);
    } finally {
      setLoadingCompleta(false);
    }
  }, [paginaCompleta, limiteCompleta, busquedaCompleta, filtroTramoCompleta, filtroTipoCompleta]);

  useEffect(() => {
    if (modalCarteraAbierto) {
      cargarCarteraCompleta();
    }
  }, [modalCarteraAbierto, cargarCarteraCompleta]);

  // 3. Abrir modal completo con tramo pre-filtrado
  const abrirDetalleTramo = (bucket) => {
    setFiltroTramoCompleta(bucket || '');
    setFiltroTipoCompleta('');
    setBusquedaCompleta('');
    setPaginaCompleta(1);
    setModalCarteraAbierto(true);
  };

  const preview1_30 = resumen?.preview1_30 || [];
  const distribucionBuckets = resumen?.distribucionBuckets || [];
  const colocacion = resumen?.colocacionMes || null;
  const historico = resumen?.historico || resumen || {};

  return (
    <div className="page-container" style={{ maxWidth: '1360px', margin: '0 auto', paddingBottom: '3rem' }}>
      
      {/* HEADER EJECUTIVO */}
      <div className="page-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary, #1e293b)' }}>
            Dashboard Ejecutivo de Cartera
          </h1>
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary, #64748b)' }}>
            Salud operativa 0–30, colocación del mes y control preventivo de cobranza
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            className="btn-secondary"
            onClick={() => cargarDashboard(true)}
            disabled={loading}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.875rem', cursor: loading ? 'not-allowed' : 'pointer' }}
            title="Actualizar información en tiempo real"
          >
            <RefreshCw size={15} className={loading ? 'spin-icon' : ''} />
            <span>{loading ? 'Cargando...' : 'Actualizar'}</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', color: '#991b1b', marginBottom: '1.5rem' }}>
          <AlertCircle size={20} />
          <div style={{ flex: 1 }}>
            <strong style={{ display: 'block', fontSize: '0.875rem' }}>No fue posible cargar el Dashboard</strong>
            <span style={{ fontSize: '0.8125rem' }}>{error}</span>
          </div>
          <button
            onClick={() => cargarDashboard(true)}
            style={{ padding: '0.375rem 0.75rem', backgroundColor: '#dc2626', color: '#ffffff', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
          >
            Reintentar
          </button>
        </div>
      )}

      {/* ================================================== */}
      {/* 1. PRIMER NIVEL: SALUD OPERATIVA 0–30 DÍAS         */}
      {/* ================================================== */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(235px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
        
        {/* A) Cartera Operativa 0-30 */}
        <div className="stat-card" style={{ padding: '1.125rem', borderRadius: '0.625rem', backgroundColor: '#ffffff', border: '1px solid #bbf7d0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ padding: '0.55rem', borderRadius: '0.5rem', backgroundColor: '#f0fdf4', color: '#16a34a' }}>
              <ShieldCheck size={22} />
            </span>
            <div style={{ width: '100%' }}>
              <p style={{ margin: 0, fontSize: '0.8125rem', color: '#166534', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.025em' }}>
                Cartera Operativa 0–30
              </p>
              {loading && !resumen ? (
                <MetricSkeleton width="130px" height="26px" />
              ) : (
                <p style={{ margin: '0.2rem 0 0 0', fontSize: '1.375rem', fontWeight: 800, color: '#14532d' }}>
                  ${Number(resumen?.carteraOperativa0_30 || resumen?.baseCartera0_30 || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              )}
              <span style={{ fontSize: '0.75rem', color: '#166534', display: 'block', marginTop: '2px', fontWeight: 500 }}>
                Cartera al corriente + preventiva
              </span>
            </div>
          </div>
        </div>

        {/* B) Créditos Operativos 0-30 */}
        <div className="stat-card" style={{ padding: '1.125rem', borderRadius: '0.625rem', backgroundColor: '#ffffff', border: '1px solid #bfdbfe', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ padding: '0.55rem', borderRadius: '0.5rem', backgroundColor: '#eff6ff', color: '#2563eb' }}>
              <CreditCard size={22} />
            </span>
            <div style={{ width: '100%' }}>
              <p style={{ margin: 0, fontSize: '0.8125rem', color: '#1e40af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.025em' }}>
                Créditos Operativos 0–30
              </p>
              {loading && !resumen ? (
                <MetricSkeleton width="80px" height="26px" />
              ) : (
                <p style={{ margin: '0.2rem 0 0 0', fontSize: '1.375rem', fontWeight: 800, color: '#1e3a8a' }}>
                  {resumen?.creditosOperativos0_30 ?? (
                    (distribucionBuckets.find(b => b.bucket === 'AL_CORRIENTE')?.creditos || 0) +
                    (distribucionBuckets.find(b => b.bucket === '1-30')?.creditos || 0)
                  )}
                </p>
              )}
              <span style={{ fontSize: '0.75rem', color: '#3b82f6', display: 'block', marginTop: '2px', fontWeight: 500 }}>
                Créditos dentro del universo prioritario
              </span>
            </div>
          </div>
        </div>

        {/* C) Clientes Operativos 0-30 */}
        <div className="stat-card" style={{ padding: '1.125rem', borderRadius: '0.625rem', backgroundColor: '#ffffff', border: '1px solid #e9d5ff', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ padding: '0.55rem', borderRadius: '0.5rem', backgroundColor: '#faf5ff', color: '#9333ea' }}>
              <Users size={22} />
            </span>
            <div style={{ width: '100%' }}>
              <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b21a8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.025em' }}>
                Clientes Operativos 0–30
              </p>
              {loading && !resumen ? (
                <MetricSkeleton width="80px" height="26px" />
              ) : (
                <p style={{ margin: '0.2rem 0 0 0', fontSize: '1.375rem', fontWeight: 800, color: '#581c87' }}>
                  {resumen?.clientesOperativos0_30 ?? '—'} <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#7e22ce' }}>clientes</span>
                </p>
              )}
              <span style={{ fontSize: '0.75rem', color: '#8b5cf6', display: 'block', marginTop: '2px', fontWeight: 500 }}>
                Clientes únicos en cartera operativa
              </span>
            </div>
          </div>
        </div>

        {/* D) Calidad 0-30 */}
        <div className="stat-card" style={{ padding: '1.125rem', borderRadius: '0.625rem', backgroundColor: '#ffffff', border: '1px solid #86efac', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <span style={{ padding: '0.55rem', borderRadius: '0.5rem', backgroundColor: '#f0fdf4', color: '#16a34a' }}>
              <TrendingUp size={22} />
            </span>
            <div style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ margin: 0, fontSize: '0.8125rem', color: '#166534', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.025em' }}>
                  Calidad 0–30
                </p>
              </div>
              {loading && !resumen ? (
                <MetricSkeleton width="100px" height="26px" />
              ) : (
                <p style={{ margin: '0.2rem 0 0 0', fontSize: '1.375rem', fontWeight: 800, color: '#15803d' }}>
                  {Number(resumen?.calidad0_30 || 0).toFixed(2)}%
                </p>
              )}
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px', lineHeight: 1.35 }}>
                <div style={{ color: '#854d0e', fontWeight: 500 }}>
                  ${Number(resumen?.saldoRiesgo1_30 || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} en riesgo
                </div>
                <div style={{ color: '#b91c1c', fontWeight: 500 }}>
                  ${Number(resumen?.saldoVencidoParaRegularizar || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} por regularizar
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ================================================== */}
      {/* 2. TARJETA DESTACADA: COLOCACIÓN DEL MES           */}
      {/* ================================================== */}
      <div style={{
        backgroundColor: '#ffffff',
        border: '1.5px solid #cbd5e1',
        borderRadius: '0.75rem',
        padding: '1.25rem 1.5rem',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)'
      }}>
        {/* Cabecera de la Tarjeta */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <span style={{ padding: '0.45rem', borderRadius: '0.375rem', backgroundColor: '#0f172a', color: '#ffffff', display: 'inline-flex' }}>
              <Calendar size={18} />
            </span>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>
                COLOCACIÓN DEL MES
              </h2>
              <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                Corte: {colocacion?.corteTexto || `${resumen?.fechaCorte || 'Operativo'} · Mes en curso`}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem' }}>
              {loading && !resumen ? (
                <MetricSkeleton width="150px" height="32px" />
              ) : (
                <strong style={{ fontSize: '1.75rem', fontWeight: 800, color: '#0f172a' }}>
                  ${Number(colocacion?.totalMonto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </strong>
              )}
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0.25rem 0.625rem',
                backgroundColor: '#0f172a',
                color: '#ffffff',
                borderRadius: '9999px',
                fontSize: '0.8125rem',
                fontWeight: 700
              }}>
                {colocacion?.totalOperaciones ?? 0} operaciones
              </span>
            </div>
            
            {/* Comparativa discreta vs periodo anterior */}
            {colocacion?.comparativa?.totalMonto && (
              <div 
                style={{ fontSize: '0.75rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'help' }}
                title={colocacion?.comparativa?.tooltipAyuda || 'Compara contra los mismos días del mes anterior.'}
              >
                <span style={{ 
                  fontWeight: 700, 
                  color: colocacion.comparativa.totalMonto.direccion === 'UP' ? '#166534' : colocacion.comparativa.totalMonto.direccion === 'DOWN' ? '#991b1b' : '#475569' 
                }}>
                  {colocacion.comparativa.totalMonto.texto}
                </span>
                <span style={{ color: '#64748b' }}>vs mismo periodo anterior</span>
                <span style={{ fontSize: '0.6875rem', color: '#94a3b8', border: '1px solid #cbd5e1', borderRadius: '50%', width: '13px', height: '13px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>?</span>
              </div>
            )}
          </div>
        </div>

        {/* 3 Segmentos Comerciales */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          
          {/* Segmento: NUEVOS */}
          <div style={{
            padding: '1rem',
            backgroundColor: '#ffffff',
            border: '1.5px solid #bfdbfe',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', fontWeight: 800, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.025em' }}>
                  <Sparkles size={13} color="#2563eb" />
                  Nuevos
                </span>
                <span style={{ fontSize: '0.75rem', padding: '0.15rem 0.45rem', backgroundColor: '#eff6ff', color: '#1e40af', borderRadius: '0.25rem', fontWeight: 700, border: '1px solid #bfdbfe' }}>
                  {colocacion?.nuevos?.participacionMonto ?? 0}%
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '1.375rem', fontWeight: 800, color: '#1e3a8a' }}>
                ${Number(colocacion?.nuevos?.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
            <div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.35rem', marginTop: '0.5rem', fontSize: '0.8125rem', color: '#475569', fontWeight: 500 }}>
                <span>{colocacion?.nuevos?.clientesUnicos ?? 0} {colocacion?.nuevos?.clientesUnicos === 1 ? 'cliente' : 'clientes'}</span>
                <span style={{ color: '#94a3b8' }}>·</span>
                <span>{colocacion?.nuevos?.operaciones ?? 0} {colocacion?.nuevos?.operaciones === 1 ? 'operación' : 'operaciones'}</span>
              </div>
              {colocacion?.comparativa?.nuevos?.monto && (
                <div 
                  style={{ marginTop: '0.45rem', paddingTop: '0.35rem', borderTop: '1px dashed #e2e8f0', fontSize: '0.75rem', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'help' }}
                  title={colocacion?.comparativa?.tooltipAyuda || 'Compara contra los mismos días del mes anterior.'}
                >
                  <span style={{ 
                    fontWeight: 600, 
                    color: colocacion.comparativa.nuevos.monto.direccion === 'UP' ? '#166534' : colocacion.comparativa.nuevos.monto.direccion === 'DOWN' ? '#991b1b' : '#64748b' 
                  }}>
                    {colocacion.comparativa.nuevos.monto.texto}
                  </span>
                  <span style={{ fontSize: '0.6875rem', color: '#94a3b8' }}>vs periodo ant.</span>
                </div>
              )}
            </div>
          </div>

          {/* Segmento: RENOVACIONES */}
          <div style={{
            padding: '1rem',
            backgroundColor: '#ffffff',
            border: '1.5px solid #86efac',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', fontWeight: 800, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.025em' }}>
                  <Repeat size={13} color="#16a34a" />
                  Renovaciones
                </span>
                <span style={{ fontSize: '0.75rem', padding: '0.15rem 0.45rem', backgroundColor: '#f0fdf4', color: '#166534', borderRadius: '0.25rem', fontWeight: 700, border: '1px solid #bbf7d0' }}>
                  {colocacion?.renovaciones?.participacionMonto ?? 0}%
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '1.375rem', fontWeight: 800, color: '#14532d' }}>
                ${Number(colocacion?.renovaciones?.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
            <div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.35rem', marginTop: '0.5rem', fontSize: '0.8125rem', color: '#475569', fontWeight: 500 }}>
                <span>{colocacion?.renovaciones?.clientesUnicos ?? 0} {colocacion?.renovaciones?.clientesUnicos === 1 ? 'cliente' : 'clientes'}</span>
                <span style={{ color: '#94a3b8' }}>·</span>
                <span>{colocacion?.renovaciones?.operaciones ?? 0} {colocacion?.renovaciones?.operaciones === 1 ? 'operación' : 'operaciones'}</span>
              </div>
              {colocacion?.comparativa?.renovaciones?.monto && (
                <div 
                  style={{ marginTop: '0.45rem', paddingTop: '0.35rem', borderTop: '1px dashed #e2e8f0', fontSize: '0.75rem', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'help' }}
                  title={colocacion?.comparativa?.tooltipAyuda || 'Compara contra los mismos días del mes anterior.'}
                >
                  <span style={{ 
                    fontWeight: 600, 
                    color: colocacion.comparativa.renovaciones.monto.direccion === 'UP' ? '#166534' : colocacion.comparativa.renovaciones.monto.direccion === 'DOWN' ? '#991b1b' : '#64748b' 
                  }}>
                    {colocacion.comparativa.renovaciones.monto.texto}
                  </span>
                  <span style={{ fontSize: '0.6875rem', color: '#94a3b8' }}>vs periodo ant.</span>
                </div>
              )}
            </div>
          </div>

          {/* Segmento: ADICIONALES */}
          <div style={{
            padding: '1rem',
            backgroundColor: '#ffffff',
            border: '1.5px solid #fde047',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', fontWeight: 800, color: '#854d0e', textTransform: 'uppercase', letterSpacing: '0.025em' }}>
                  <PlusCircle size={13} color="#ca8a04" />
                  Adicionales
                </span>
                <span style={{ fontSize: '0.75rem', padding: '0.15rem 0.45rem', backgroundColor: '#fef9c3', color: '#854d0e', borderRadius: '0.25rem', fontWeight: 700, border: '1px solid #fde047' }}>
                  {colocacion?.adicionales?.participacionMonto ?? 0}%
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '1.375rem', fontWeight: 800, color: '#713f12' }}>
                ${Number(colocacion?.adicionales?.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
            <div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.35rem', marginTop: '0.5rem', fontSize: '0.8125rem', color: '#475569', fontWeight: 500 }}>
                <span>{colocacion?.adicionales?.clientesUnicos ?? 0} {colocacion?.adicionales?.clientesUnicos === 1 ? 'cliente' : 'clientes'}</span>
                <span style={{ color: '#94a3b8' }}>·</span>
                <span>{colocacion?.adicionales?.operaciones ?? 0} {colocacion?.adicionales?.operaciones === 1 ? 'operación' : 'operaciones'}</span>
              </div>
              {colocacion?.comparativa?.adicionales?.monto && (
                <div 
                  style={{ marginTop: '0.45rem', paddingTop: '0.35rem', borderTop: '1px dashed #e2e8f0', fontSize: '0.75rem', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'help' }}
                  title={colocacion?.comparativa?.tooltipAyuda || 'Compara contra los mismos días del mes anterior.'}
                >
                  <span style={{ 
                    fontWeight: 600, 
                    color: colocacion.comparativa.adicionales.monto.direccion === 'UP' ? '#166534' : colocacion.comparativa.adicionales.monto.direccion === 'DOWN' ? '#991b1b' : '#64748b' 
                  }}>
                    {colocacion.comparativa.adicionales.monto.texto}
                  </span>
                  <span style={{ fontSize: '0.6875rem', color: '#94a3b8' }}>vs periodo ant.</span>
                </div>
              )}
            </div>
          </div>

          {/* Segmento Opcional: POR CLASIFICAR (sólo si > 0) */}
          {colocacion?.porClasificar?.operaciones > 0 && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#ffffff',
              border: '1.5px solid #e2e8f0',
              borderRadius: '0.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b' }}>Por clasificar</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', padding: '0.15rem 0.45rem', backgroundColor: '#f1f5f9', borderRadius: '0.25rem' }}>
                    {colocacion.porClasificar.participacionMonto}%
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: '1.375rem', fontWeight: 800, color: '#334155' }}>
                  ${Number(colocacion.porClasificar.monto).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </p>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.35rem', marginTop: '0.5rem', fontSize: '0.8125rem', color: '#64748b' }}>
                <span>{colocacion.porClasificar.clientesUnicos ?? 0} {colocacion.porClasificar.clientesUnicos === 1 ? 'cliente' : 'clientes'}</span>
                <span style={{ color: '#94a3b8' }}>·</span>
                <span>{colocacion.porClasificar.operaciones} {colocacion.porClasificar.operaciones === 1 ? 'operación' : 'operaciones'}</span>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ================================================== */}
      {/* 3. SEGUNDO NIVEL: PREVENCIÓN Y TRAMOS DE ATRASO    */}
      {/* ================================================== */}
      <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '0.625rem', padding: '1.25rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Layers size={19} color="#2563eb" />
              DISTRIBUCIÓN POR TRAMOS DE ATRASO
            </h3>
            <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.8125rem', color: '#64748b' }}>
              Haga clic en un tramo para abrir la cartera correspondiente en la vista completa
            </p>
          </div>
          <button
            onClick={() => abrirDetalleTramo('')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.4rem 0.75rem',
              backgroundColor: '#eff6ff',
              color: '#1d4ed8',
              border: '1px solid #bfdbfe',
              borderRadius: '0.375rem',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            <span>Ver Cartera Completa</span>
            <ArrowRight size={14} />
          </button>
        </div>

        {/* Barra Visual de Distribución */}
        <div style={{ display: 'flex', height: '14px', borderRadius: '7px', overflow: 'hidden', backgroundColor: '#f1f5f9', marginBottom: '1rem' }}>
          {distribucionBuckets.map(b => (
            <div 
              key={b.bucket}
              title={`${TRAMO_LABELS[b.bucket] || b.label}: ${b.creditos} créditos ($${b.saldo.toLocaleString('es-MX')}) - ${b.porcentajeSaldo}%`}
              style={{
                width: `${Math.max(b.porcentajeSaldo, b.creditos > 0 ? 2 : 0)}%`,
                backgroundColor: TRAMO_COLORS[b.bucket]?.bar || '#94a3b8',
                transition: 'width 0.3s ease'
              }}
            />
          ))}
        </div>

        {/* Tarjetas de Tramos Clickeables */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
          {loading && !resumen ? (
            [1, 2, 3, 4, 5].map(i => (
              <div key={i} style={{ padding: '0.75rem', borderRadius: '0.375rem', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <MetricSkeleton width="70px" height="14px" />
                <MetricSkeleton width="90px" height="20px" />
              </div>
            ))
          ) : (
            distribucionBuckets.map(b => {
              const colors = TRAMO_COLORS[b.bucket] || { bg: '#f1f5f9', text: '#334155', border: '#e2e8f0' };
              const labelTramo = TRAMO_LABELS[b.bucket] || b.label;
              return (
                <button
                  key={b.bucket}
                  onClick={() => abrirDetalleTramo(b.bucket)}
                  title={`Clic: abrir vista completa del tramo ${labelTramo}`}
                  style={{
                    padding: '0.75rem',
                    borderRadius: '0.375rem',
                    backgroundColor: colors.bg,
                    border: `1.5px solid ${colors.border}`,
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: colors.text }}>{labelTramo}</span>
                    <span style={{ fontSize: '0.6875rem', padding: '0.1rem 0.35rem', borderRadius: '0.2rem', backgroundColor: '#ffffff', color: colors.text, fontWeight: 700 }}>
                      {b.creditos}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 700, color: '#0f172a' }}>
                    ${Number(b.saldo || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.2rem' }}>
                    <span style={{ fontSize: '0.6875rem', color: '#64748b' }}>{b.porcentajeSaldo}% de cartera</span>
                    <span style={{ fontSize: '0.6875rem', color: colors.text, fontWeight: 700 }}>Ver tramo &rarr;</span>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* ====================================================================== */}
      {/* 4. BLOQUE ACCIONABLE: CARTERA PRIORITARIA 1–30 DÍAS (TOP 5)            */}
      {/* ====================================================================== */}
      <div className="dashboard-section" style={{ backgroundColor: '#ffffff', border: '1.5px solid #fde047', borderRadius: '0.625rem', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ padding: '0.35rem', borderRadius: '0.375rem', backgroundColor: '#fef9c3', color: '#854d0e', display: 'inline-flex' }}>
                <AlertTriangle size={18} />
              </span>
              <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 800, color: '#854d0e' }}>
                CARTERA PRIORITARIA 1–30 DÍAS
              </h2>
              <span style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '0.15rem 0.45rem', backgroundColor: '#fef9c3', color: '#854d0e', borderRadius: '0.25rem', border: '1px solid #fde047' }}>
                Acción Preventiva
              </span>
            </div>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8125rem', color: '#713f12' }}>
              Saldo en riesgo: <strong>${Number(resumen?.saldoRiesgo1_30 || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong> · Vencido para regularizar: <strong style={{ color: '#b91c1c' }}>${Number(resumen?.saldoVencidoParaRegularizar || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong>
            </p>
          </div>

          <button
            onClick={() => abrirDetalleTramo('1-30')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.4rem 0.875rem',
              backgroundColor: '#ca8a04',
              color: '#ffffff',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.75rem',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            <span>VER CARTERA 1–30</span>
            <ExternalLink size={13} />
          </button>
        </div>

        {loading && !resumen ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
            <RefreshCw size={20} className="spin-icon" style={{ display: 'block', margin: '0 auto 0.5rem' }} />
            Cargando cartera preventiva...
          </div>
        ) : preview1_30.length === 0 ? (
          <div className="empty-state" style={{ padding: '1.5rem', textAlign: 'center', color: '#64748b' }}>
            <p>No hay créditos en mora preventiva en el tramo de 1 a 30 días.</p>
          </div>
        ) : (
          <div className="table-container" style={{ overflowX: 'auto', borderRadius: '0.375rem', border: '1px solid #fef08a' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.8125rem' }}>
              <thead style={{ backgroundColor: '#fefce8', borderBottom: '1px solid #fef08a' }}>
                <tr>
                  <th style={{ padding: '0.625rem 0.875rem', fontWeight: 700, color: '#854d0e' }}>Folio</th>
                  <th style={{ padding: '0.625rem 0.875rem', fontWeight: 700, color: '#854d0e' }}>Cliente</th>
                  <th style={{ padding: '0.625rem 0.875rem', fontWeight: 700, color: '#854d0e' }}>Tipo / Grupo</th>
                  <th style={{ padding: '0.625rem 0.875rem', fontWeight: 700, textAlign: 'center', color: '#854d0e' }}>Días atraso</th>
                  <th style={{ padding: '0.625rem 0.875rem', fontWeight: 700, textAlign: 'center', color: '#854d0e' }}>Doc vencidos</th>
                  <th style={{ padding: '0.625rem 0.875rem', fontWeight: 700, textAlign: 'right', color: '#854d0e' }} title="Cuotas vencidas pendientes + moratorio efectivo">Vencido exigible</th>
                  <th style={{ padding: '0.625rem 0.875rem', fontWeight: 700, textAlign: 'right', color: '#854d0e' }} title="Saldo contractual pendiente + moratorio efectivo">Exigible total</th>
                  <th style={{ padding: '0.625rem 0.875rem', fontWeight: 700, textAlign: 'center', color: '#854d0e' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {preview1_30.map((c) => (
                  <tr key={c.creditoId} style={{ borderBottom: '1px solid #fef9c3', backgroundColor: '#ffffff' }}>
                    <td style={{ padding: '0.625rem 0.875rem', fontFamily: 'monospace', fontWeight: 700, color: '#1e40af' }}>
                      {c.folio}
                    </td>
                    <td style={{ padding: '0.625rem 0.875rem', fontWeight: 600 }}>
                      {c.cliente?.nombre || '—'}
                    </td>
                    <td style={{ padding: '0.625rem 0.875rem' }}>
                      <span style={{ display: 'inline-block', padding: '0.15rem 0.4rem', borderRadius: '0.25rem', fontSize: '0.6875rem', fontWeight: 600, backgroundColor: '#fef9c3', color: '#854d0e' }}>
                        {c.tipoCredito}
                      </span>
                      {c.nombreGrupo && (
                        <span style={{ display: 'block', fontSize: '0.6875rem', color: '#64748b', marginTop: '0.1rem' }}>
                          {c.nombreGrupo}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '0.625rem 0.875rem', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.2rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        backgroundColor: '#fef9c3',
                        color: '#854d0e',
                        border: '1px solid #fde047'
                      }}>
                        {c.diasVencidos} d
                      </span>
                    </td>
                    <td style={{ padding: '0.625rem 0.875rem', textAlign: 'center', fontWeight: 600 }}>
                      {c.docVencidos}
                    </td>
                    <td style={{ padding: '0.625rem 0.875rem', textAlign: 'right', fontWeight: 700, color: '#a16207' }}>
                      ${Number(c.saldoVencido || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: '0.625rem 0.875rem', textAlign: 'right', fontWeight: 600 }}>
                      ${Number(c.saldoTotal || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: '0.625rem 0.875rem', textAlign: 'center' }}>
                      <button
                        onClick={() => navigate(`/creditos/${c.creditoId}`)}
                        style={{
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          borderRadius: '0.25rem',
                          backgroundColor: '#eff6ff',
                          color: '#1d4ed8',
                          border: '1px solid #bfdbfe',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <span>Ficha</span>
                        <ExternalLink size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ====================================================================== */}
      {/* 5. BLOQUE SECUNDARIO: INFORMACIÓN HISTÓRICA Y RESUMEN GENERAL          */}
      {/* ====================================================================== */}
      <div style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '0.625rem',
        padding: '1.25rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setHistoricoAbierto(!historicoAbierto)}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Clock size={18} color="#64748b" />
              INFORMACIÓN HISTÓRICA Y RESUMEN GENERAL DE CARTERA
            </h3>
            <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.8125rem', color: '#64748b' }}>
              Saldos acumulados, cartera global y conciliación contable histórica
            </p>
          </div>
          <button
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.8125rem',
              fontWeight: 600
            }}
          >
            <span>{historicoAbierto ? 'Ocultar' : 'Ver detalle'}</span>
            {historicoAbierto ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>

        {historicoAbierto && (
          <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0' }}>
            
            {/* Grid de Métricas Históricas */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.875rem', marginBottom: '1.25rem' }}>
              
              <div style={{ padding: '0.875rem', backgroundColor: '#f8fafc', borderRadius: '0.375rem', border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Saldo total de cartera</span>
                <p style={{ margin: '0.2rem 0 0 0', fontSize: '1.125rem', fontWeight: 700, color: '#0f172a' }}>
                  ${Number(historico.saldoActualCartera || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </p>
                <span style={{ fontSize: '0.6875rem', color: '#64748b' }}>{historico.creditosConSaldo || 0} créditos con saldo total</span>
              </div>

              <div style={{ padding: '0.875rem', backgroundColor: '#fef2f2', borderRadius: '0.375rem', border: '1px solid #fecaca' }}>
                <span style={{ fontSize: '0.75rem', color: '#991b1b', fontWeight: 600 }}>Saldo vencido total</span>
                <p style={{ margin: '0.2rem 0 0 0', fontSize: '1.125rem', fontWeight: 700, color: '#b91c1c' }}>
                  ${Number(historico.saldoVencidoReal || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </p>
                <span style={{ fontSize: '0.6875rem', color: '#991b1b' }}>Suma cuotas con fecha vencida</span>
              </div>

              <div style={{ padding: '0.875rem', backgroundColor: '#f0fdf4', borderRadius: '0.375rem', border: '1px solid #bbf7d0' }}>
                <span style={{ fontSize: '0.75rem', color: '#166534', fontWeight: 600 }}>Saldo por vencer total</span>
                <p style={{ margin: '0.2rem 0 0 0', fontSize: '1.125rem', fontWeight: 700, color: '#15803d' }}>
                  ${Number(historico.saldoPorVencer || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </p>
                <span style={{ fontSize: '0.6875rem', color: '#166534' }}>Cuotas no vencidas</span>
              </div>

              <div style={{ padding: '0.875rem', backgroundColor: '#f8fafc', borderRadius: '0.375rem', border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Monto colocado histórico</span>
                <p style={{ margin: '0.2rem 0 0 0', fontSize: '1.125rem', fontWeight: 700, color: '#0f172a' }}>
                  ${Number(historico.montoColocadoOriginal || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </p>
                <span style={{ fontSize: '0.6875rem', color: '#64748b' }}>{historico.creditosTotales || 0} créditos históricos</span>
              </div>

              <div style={{ padding: '0.875rem', backgroundColor: '#f8fafc', borderRadius: '0.375rem', border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Clientes con saldo total</span>
                <p style={{ margin: '0.2rem 0 0 0', fontSize: '1.125rem', fontWeight: 700, color: '#0f172a' }}>
                  {historico.clientesConSaldo || 0}
                </p>
                <span style={{ fontSize: '0.6875rem', color: '#64748b' }}>De {historico.totalClientes || 0} clientes registrados</span>
              </div>

            </div>

            {/* Conciliación Contable Inmediata */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', padding: '0.75rem 1rem', backgroundColor: '#f8fafc', borderRadius: '0.375rem', border: '1px solid #e2e8f0', fontSize: '0.8125rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#475569' }}>Saldo total de cartera:</span>
                <strong style={{ color: '#0f172a' }}>${Number(historico.saldoActualCartera || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b' }}>
                <span>= Saldo vencido real:</span>
                <span>${Number(historico.saldoVencidoReal || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b' }}>
                <span>+ Saldo por vencer:</span>
                <span>${Number(historico.saldoPorVencer || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #cbd5e1', paddingTop: '0.35rem', marginTop: '0.2rem' }}>
                <span style={{ color: '#166534', fontWeight: 600 }}>Delta contable:</span>
                <strong style={{ color: '#16a34a' }}>$0.00 (Cuadrado exacto)</strong>
              </div>
            </div>

          </div>
        )}
      </div>

      {/* ====================================================================== */}
      {/* MODAL / DRAWER COMPLETO: CARTERA PAGINADA (10/25/50)                   */}
      {/* ====================================================================== */}
      {modalCarteraAbierto && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '1.5rem'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '0.75rem',
            width: '100%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden'
          }}>
            
            {/* Modal Header */}
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
                  Cartera Completa — Vista Detallada
                </h2>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8125rem', color: '#64748b' }}>
                  {paginacionCompleta.total} créditos evaluados | Visualización paginada y filtrada
                </p>
              </div>
              <button
                onClick={() => setModalCarteraAbierto(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '0.25rem' }}
                title="Cerrar ventana"
              >
                <X size={22} />
              </button>
            </div>

            {/* Modal Filters & Search Bar */}
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', backgroundColor: '#ffffff' }}>
              
              {/* Buscador */}
              <div style={{ position: 'relative', flex: '1 1 240px' }}>
                <Search size={16} style={{ position: 'absolute', left: '0.625rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  type="text"
                  placeholder="Buscar por folio, cliente o grupo..."
                  value={busquedaCompleta}
                  onChange={(e) => {
                    setBusquedaCompleta(e.target.value);
                    setPaginaCompleta(1);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem 0.5rem 2.25rem',
                    fontSize: '0.8125rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #cbd5e1',
                    outline: 'none'
                  }}
                />
              </div>

              {/* Filtro Tramo */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.35rem 0.6rem', backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '0.375rem', fontSize: '0.8125rem' }}>
                <span>Tramo:</span>
                <select 
                  value={filtroTramoCompleta} 
                  onChange={(e) => {
                    setFiltroTramoCompleta(e.target.value);
                    setPaginaCompleta(1);
                  }}
                  style={{ border: 'none', background: 'transparent', fontSize: '0.8125rem', fontWeight: 600, outline: 'none', cursor: 'pointer' }}
                >
                  <option value="">TODOS</option>
                  <option value="AL_CORRIENTE">Al corriente (0 días)</option>
                  <option value="1-30">1–30 días</option>
                  <option value="31-60">31–60 días</option>
                  <option value="61-90">61–90 días</option>
                  <option value="91+">91+ días</option>
                </select>
              </div>

              {/* Filtro Tipo */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.35rem 0.6rem', backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '0.375rem', fontSize: '0.8125rem' }}>
                <Filter size={13} />
                <span>Tipo:</span>
                <select 
                  value={filtroTipoCompleta} 
                  onChange={(e) => {
                    setFiltroTipoCompleta(e.target.value);
                    setPaginaCompleta(1);
                  }}
                  style={{ border: 'none', background: 'transparent', fontSize: '0.8125rem', fontWeight: 600, outline: 'none', cursor: 'pointer' }}
                >
                  <option value="">TODOS</option>
                  <option value="INDIVIDUAL">INDIVIDUAL</option>
                  <option value="GRUPAL">GRUPAL</option>
                  <option value="COLOCADORA">COLOCADORA</option>
                </select>
              </div>

              {/* Registros por página */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.35rem 0.6rem', backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '0.375rem', fontSize: '0.8125rem' }}>
                <span>Mostrar:</span>
                <select 
                  value={limiteCompleta} 
                  onChange={(e) => {
                    setLimiteCompleta(parseInt(e.target.value, 10));
                    setPaginaCompleta(1);
                  }}
                  style={{ border: 'none', background: 'transparent', fontSize: '0.8125rem', fontWeight: 600, outline: 'none', cursor: 'pointer' }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>

            </div>

            {/* Modal Body Table */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.5rem' }}>
              {loadingCompleta ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                  <RefreshCw size={24} className="spin-icon" style={{ display: 'block', margin: '0 auto 0.75rem' }} />
                  Cargando cartera paginada...
                </div>
              ) : carteraCompleta.length === 0 ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                  No se encontraron créditos que coincidan con la búsqueda o filtros.
                </div>
              ) : (
                <div className="table-container" style={{ overflowX: 'auto', borderRadius: '0.375rem', border: '1px solid #e2e8f0' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.8125rem' }}>
                    <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
                      <tr>
                        <th style={{ padding: '0.625rem 0.875rem', fontWeight: 600 }}>Folio</th>
                        <th style={{ padding: '0.625rem 0.875rem', fontWeight: 600 }}>Cliente</th>
                        <th style={{ padding: '0.625rem 0.875rem', fontWeight: 600 }}>Tipo / Grupo</th>
                        <th style={{ padding: '0.625rem 0.875rem', fontWeight: 600, textAlign: 'center' }}>Días atraso</th>
                        <th style={{ padding: '0.625rem 0.875rem', fontWeight: 600, textAlign: 'center' }}>Doc venc</th>
                        <th style={{ padding: '0.625rem 0.875rem', fontWeight: 600, textAlign: 'right' }}>Saldo vencido</th>
                        <th style={{ padding: '0.625rem 0.875rem', fontWeight: 600, textAlign: 'right' }}>Saldo total</th>
                        <th style={{ padding: '0.625rem 0.875rem', fontWeight: 600, textAlign: 'center' }}>Tramo</th>
                        <th style={{ padding: '0.625rem 0.875rem', fontWeight: 600, textAlign: 'center' }}>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {carteraCompleta.map((c) => {
                        const colors = TRAMO_COLORS[c.bucket] || { bg: '#f1f5f9', text: '#334155', border: '#cbd5e1' };
                        const labelTramo = TRAMO_LABELS[c.bucket] || c.bucket;
                        return (
                          <tr key={c.creditoId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '0.625rem 0.875rem', fontFamily: 'monospace', fontWeight: 700, color: '#1e40af' }}>
                              {c.folio}
                            </td>
                            <td style={{ padding: '0.625rem 0.875rem', fontWeight: 500 }}>
                              {c.cliente?.nombre || '—'}
                            </td>
                            <td style={{ padding: '0.625rem 0.875rem' }}>
                              <span style={{ display: 'inline-block', padding: '0.15rem 0.4rem', borderRadius: '0.25rem', fontSize: '0.6875rem', fontWeight: 600, backgroundColor: '#f1f5f9', color: '#334155' }}>
                                {c.tipoCredito}
                              </span>
                              {c.nombreGrupo && (
                                <span style={{ display: 'block', fontSize: '0.6875rem', color: '#64748b', marginTop: '0.1rem' }}>
                                  {c.nombreGrupo}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '0.625rem 0.875rem', textAlign: 'center' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '0.2rem 0.5rem',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                backgroundColor: c.diasVencidos === 0 ? '#dcfce7' : '#fee2e2',
                                color: c.diasVencidos === 0 ? '#166534' : '#991b1b'
                              }}>
                                {c.diasVencidos} d
                              </span>
                            </td>
                            <td style={{ padding: '0.625rem 0.875rem', textAlign: 'center', fontWeight: 600 }}>
                              {c.docVencidos}
                            </td>
                            <td style={{ padding: '0.625rem 0.875rem', textAlign: 'right', fontWeight: 700, color: c.saldoVencido > 0 ? '#b91c1c' : '#15803d' }}>
                              ${Number(c.saldoVencido || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td style={{ padding: '0.625rem 0.875rem', textAlign: 'right', fontWeight: 600 }}>
                              ${Number(c.saldoTotal || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td style={{ padding: '0.625rem 0.875rem', textAlign: 'center' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '0.2rem 0.5rem',
                                borderRadius: '0.25rem',
                                fontSize: '0.6875rem',
                                fontWeight: 700,
                                backgroundColor: colors.bg,
                                color: colors.text,
                                border: `1px solid ${colors.border}`
                              }}>
                                {labelTramo}
                              </span>
                            </td>
                            <td style={{ padding: '0.625rem 0.875rem', textAlign: 'center' }}>
                              <button
                                onClick={() => {
                                  setModalCarteraAbierto(false);
                                  navigate(`/creditos/${c.creditoId}`);
                                }}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  borderRadius: '0.25rem',
                                  backgroundColor: '#eff6ff',
                                  color: '#1d4ed8',
                                  border: '1px solid #bfdbfe',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}
                              >
                                <span>Ficha</span>
                                <ExternalLink size={12} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Modal Pagination Footer */}
            <div style={{ padding: '0.875rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' }}>
              <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                Página {paginaCompleta} de {paginacionCompleta.totalPages || 1} ({paginacionCompleta.total} créditos en total)
              </span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setPaginaCompleta(p => Math.max(1, p - 1))}
                  disabled={paginaCompleta <= 1 || loadingCompleta}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.35rem 0.75rem',
                    fontSize: '0.8125rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                    cursor: paginaCompleta <= 1 ? 'not-allowed' : 'pointer',
                    opacity: paginaCompleta <= 1 ? 0.5 : 1
                  }}
                >
                  <ChevronLeft size={16} />
                  <span>Anterior</span>
                </button>
                <button
                  onClick={() => setPaginaCompleta(p => Math.min(paginacionCompleta.totalPages || 1, p + 1))}
                  disabled={paginaCompleta >= paginacionCompleta.totalPages || loadingCompleta}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.35rem 0.75rem',
                    fontSize: '0.8125rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                    cursor: paginaCompleta >= paginacionCompleta.totalPages ? 'not-allowed' : 'pointer',
                    opacity: paginaCompleta >= paginacionCompleta.totalPages ? 0.5 : 1
                  }}
                >
                  <span>Siguiente</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}

export default Dashboard;
